import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellOutlined,
  ReloadOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  CheckOutlined,
  CloseOutlined,
  MoreOutlined,
  UserOutlined,
  FileTextOutlined,
  TagOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { actionApi } from '../../utils/actionApi';
import { getSuccessMessage } from '../../utils/actionMessageTranslator';
import {
  message,
  Spin,
  Input,
  Button,
  Select,
  Card,
  Row,
  Col,
  Space,
  Popconfirm,
  Tooltip,
  Badge,
  Dropdown,
  Empty,
  Tag,
  Avatar,
  Typography,
} from 'antd';
import { CardListSkeleton } from '../../components/SkeletonLoaders';
import PersianDatePicker from '../../components/PersianDatePicker';
import { toGregorian, toJalali } from '../../utils/dateUtils';

const Notifications = () => {
  const navigate = useNavigate();
  const { actionToken } = useAuth();
  const { unreadCount: contextUnreadCount, refreshUnreadCount } = useNotification();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    read: '',
    type: '',
    action_id: '',
    date_from: '',
    date_to: '',
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
  });

  // انواع اعلان‌ها
  const notificationTypes = [
    { value: '', label: 'همه انواع' },
    { value: 'action_assigned', label: 'تخصیص اقدام' },
    { value: 'explanation_requested', label: 'درخواست توضیح' },
    { value: 'deadline_approaching', label: 'نزدیک شدن به مهلت' },
    { value: 'action_overdue', label: 'اقدام معوق' },
    { value: 'file_uploaded', label: 'آپلود فایل' },
    { value: 'status_changed', label: 'تغییر وضعیت' },
    { value: 'action_closed', label: 'بسته شدن اقدام' },
    { value: 'action_item_added', label: 'افزودن آیتم اقدام' },
    { value: 'comment_added', label: 'افزودن نظر' },
    { value: 'comment_mentioned', label: 'ذکر در نظر' },
    { value: 'request_created', label: 'ایجاد درخواست' },
    { value: 'request_approved', label: 'تأیید درخواست' },
    { value: 'request_rejected', label: 'رد درخواست' },
  ];

  // دریافت لیست اعلان‌ها
  const fetchNotifications = async (page = 1) => {
    if (!actionToken) return;

    setLoading(true);
    try {
      const filterParams = {
        page,
        per_page: pagination.per_page,
      };

      if (filters.read !== '') {
        filterParams.read = filters.read === 'true';
      }
      if (filters.type) {
        filterParams.type = filters.type;
      }
      if (filters.action_id) {
        filterParams.action_id = parseInt(filters.action_id);
      }
      if (filters.date_from) {
        filterParams.date_from = filters.date_from;
      }
      if (filters.date_to) {
        filterParams.date_to = filters.date_to;
      }

      const res = await actionApi.getNotifications(filterParams);
      const list = res?.data?.data || [];
      const pag = res?.data?.pagination || pagination;
      setNotifications(list);
      setPagination(pag);
    } catch (error) {
      console.error('Load notifications error:', error);
      message.error('خطا در دریافت لیست اعلان‌ها');
    } finally {
      setLoading(false);
    }
  };

  // دریافت تعداد اعلان‌های خوانده نشده
  const fetchUnreadCount = async () => {
    if (!actionToken) return;
    try {
      const res = await actionApi.getUnreadCount();
      const count = res?.data?.unread_count || 0;
      setUnreadCount(count);
      // به‌روزرسانی context
      refreshUnreadCount();
    } catch (error) {
      console.error('Load unread count error:', error);
    }
  };

  useEffect(() => {
    if (actionToken) {
      fetchNotifications(1);
      fetchUnreadCount();
    }
  }, [actionToken]);

  const handleRefresh = () => {
    fetchNotifications(pagination.current_page || 1);
    fetchUnreadCount();
    refreshUnreadCount();
  };

  // علامت‌گذاری همه به عنوان خوانده شده
  const handleMarkAllAsRead = async () => {
    try {
      const res = await actionApi.markAllAsRead();
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'همه اعلان‌ها به عنوان خوانده شده علامت‌گذاری شدند'));
        fetchNotifications(pagination.current_page || 1);
        fetchUnreadCount();
        refreshUnreadCount();
      }
    } catch (error) {
      console.error('Mark all as read error:', error);
      message.error('خطا در علامت‌گذاری اعلان‌ها');
    }
  };

  // علامت‌گذاری یک اعلان به عنوان خوانده شده
  const handleMarkAsRead = async (notificationId) => {
    try {
      const res = await actionApi.markNotificationAsRead(notificationId);
      if (res?.success) {
        message.success('اعلان به عنوان خوانده شده علامت‌گذاری شد');
        fetchNotifications(pagination.current_page || 1);
        fetchUnreadCount();
        refreshUnreadCount();
      }
    } catch (error) {
      console.error('Mark as read error:', error);
      message.error('خطا در علامت‌گذاری اعلان');
    }
  };

  // علامت‌گذاری یک اعلان به عنوان خوانده نشده
  const handleMarkAsUnread = async (notificationId) => {
    try {
      const res = await actionApi.markNotificationAsUnread(notificationId);
      if (res?.success) {
        message.success('اعلان به عنوان خوانده نشده علامت‌گذاری شد');
        fetchNotifications(pagination.current_page || 1);
        fetchUnreadCount();
        refreshUnreadCount();
      }
    } catch (error) {
      console.error('Mark as unread error:', error);
      message.error('خطا در علامت‌گذاری اعلان');
    }
  };

  // حذف اعلان
  const handleDelete = async (notificationId) => {
    try {
      const res = await actionApi.deleteNotification(notificationId);
      if (res?.success) {
        message.success('اعلان حذف شد');
        fetchNotifications(pagination.current_page || 1);
        fetchUnreadCount();
        refreshUnreadCount();
      }
    } catch (error) {
      console.error('Delete notification error:', error);
      message.error('خطا در حذف اعلان');
    }
  };

  // مشاهده جزئیات اعلان
  const handleViewDetails = async (notification) => {
    try {
      const res = await actionApi.getNotification(notification.id);
      if (res?.success && res.data) {
        // اگر اعلان خوانده نشده است، آن را به عنوان خوانده شده علامت‌گذاری کن
        if (!notification.read_at) {
          await handleMarkAsRead(notification.id);
        }
        // اگر action_id در data وجود دارد، به صفحه اقدام برو
        if (res.data.data?.action_id) {
          navigate(`/action/${res.data.data.action_id}`);
        }
      }
    } catch (error) {
      console.error('View notification error:', error);
      message.error('خطا در دریافت جزئیات اعلان');
    }
  };

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // فیلتر خودکار با debounce
  useEffect(() => {
    if (!actionToken) return;
    const timer = setTimeout(() => {
      fetchNotifications(1);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const resetFilters = () => {
    setFilters({
      read: '',
      type: '',
      action_id: '',
      date_from: '',
      date_to: '',
    });
  };

  // فرمت تاریخ
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return toJalali(dateString, 'jYYYY/jMM/jDD HH:mm');
    } catch {
      return dateString;
    }
  };

  // دریافت برچسب نوع اعلان
  const getNotificationTypeLabel = (type) => {
    const found = notificationTypes.find((t) => t.value === type);
    return found ? found.label : type;
  };

  // دریافت توضیحات اعلان بر اساس نوع
  const getNotificationDescription = (notification) => {
    const { notification_type, action_title, action_number, triggered_by_name, metadata } = notification;
    
    if (!notification_type) return null;

    switch (notification_type) {
      case 'action_assigned':
        return `اقدام "${action_title || action_number}" به شما اختصاص داده شد`;
      case 'action_item_added':
        return `آیتم "${metadata?.action_item_title || 'جدید'}" به اقدام "${action_title || action_number}" اضافه شد`;
      case 'comment_added':
        return `${triggered_by_name || 'کاربری'} نظر جدیدی در اقدام "${action_title || action_number}" اضافه کرد`;
      case 'comment_mentioned':
        return `${triggered_by_name || 'کاربری'} شما را در یک نظر در اقدام "${action_title || action_number}" ذکر کرد`;
      case 'deadline_approaching':
        return `موعد اقدام "${action_title || action_number}" نزدیک است`;
      case 'action_overdue':
        return `اقدام "${action_title || action_number}" معوق شده است`;
      case 'file_uploaded':
        return `فایل جدیدی در اقدام "${action_title || action_number}" آپلود شد`;
      case 'status_changed':
        return `وضعیت اقدام "${action_title || action_number}" تغییر کرد`;
      case 'action_closed':
        return `اقدام "${action_title || action_number}" بسته شد`;
      case 'request_created':
        return `درخواست جدیدی برای اقدام "${action_title || action_number}" ایجاد شد`;
      case 'request_approved':
        return `درخواست مربوط به اقدام "${action_title || action_number}" تأیید شد`;
      case 'request_rejected':
        return `درخواست مربوط به اقدام "${action_title || action_number}" رد شد`;
      case 'explanation_requested':
        return `درخواست توضیح برای اقدام "${action_title || action_number}"`;
      default:
        return null;
    }
  };

  // دریافت رنگ بر اساس نوع اعلان
  const getNotificationTypeColor = (type) => {
    const colorMap = {
      action_assigned: 'blue',
      explanation_requested: 'orange',
      deadline_approaching: 'gold',
      action_overdue: 'red',
      file_uploaded: 'cyan',
      status_changed: 'purple',
      action_closed: 'green',
      action_item_added: 'blue',
      comment_added: 'geekblue',
      comment_mentioned: 'lime',
      request_created: 'orange',
      request_approved: 'green',
      request_rejected: 'red',
    };
    return colorMap[type] || 'default';
  };

  const isEmpty = !loading && (!notifications || notifications.length === 0);

  return (
    <MainLayout>
      <div className="px-4 md:px-6 py-5 w-full">
        {/* هدر */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-400 via-indigo-400 to-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <BellOutlined className="text-white text-xl" />
                </div>
                {unreadCount > 0 && (
                  <Badge
                    count={unreadCount}
                    className="absolute -top-1 -right-1"
                    style={{ backgroundColor: '#ef4444' }}
                  />
                )}
              </div>
              <div>
                <h1
                  className="text-2xl font-bold text-gray-900 dark:text-white"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  اعلان‌ها
                </h1>
                <p
                  className="text-sm text-gray-500 dark:text-gray-400 mt-0.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  {pagination.total > 0
                    ? `${pagination.total} اعلان${unreadCount > 0 ? ` (${unreadCount} خوانده نشده)` : ''}`
                    : 'مدیریت اعلان‌های سیستم'}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Button
                onClick={handleRefresh}
                icon={<ReloadOutlined />}
                className="flex items-center justify-center"
              >
                به‌روزرسانی
              </Button>
              {unreadCount > 0 && (
                <Button
                  onClick={handleMarkAllAsRead}
                  icon={<CheckCircleOutlined />}
                  type="primary"
                  className="bg-emerald-500 hover:bg-emerald-600 border-emerald-500 hover:border-emerald-600"
                >
                  علامت‌گذاری همه به عنوان خوانده شده
                </Button>
              )}
            </div>
          </div>

          {/* پنل فیلتر */}
          <Card
            className="mb-6"
            title={
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="w-full flex items-center justify-between hover:opacity-80 transition-opacity"
                style={{ fontFamily: 'CustomFont' }}
              >
                <div className="flex items-center gap-2">
                  <FilterOutlined />
                  <span>فیلترها</span>
                </div>
                <span className="text-xs text-gray-500">
                  {filtersOpen ? 'بستن' : 'باز کردن'}
                </span>
              </button>
            }
          >
            {filtersOpen && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    وضعیت خوانده شدن
                  </label>
                  <Select
                    value={filters.read}
                    onChange={(value) => setFilter('read', value)}
                    className="w-full"
                    placeholder="همه"
                  >
                    <Select.Option value="">همه</Select.Option>
                    <Select.Option value="false">خوانده نشده</Select.Option>
                    <Select.Option value="true">خوانده شده</Select.Option>
                  </Select>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    نوع اعلان
                  </label>
                  <Select
                    value={filters.type}
                    onChange={(value) => setFilter('type', value)}
                    className="w-full"
                    placeholder="همه انواع"
                  >
                    {notificationTypes.map((type) => (
                      <Select.Option key={type.value} value={type.value}>
                        {type.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    شناسه اقدام
                  </label>
                  <Input
                    value={filters.action_id}
                    onChange={(e) => setFilter('action_id', e.target.value)}
                    placeholder="شناسه اقدام"
                    type="number"
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    بازه زمانی
                  </label>
                  <PersianDatePicker
                    range={true}
                    placeholder="انتخاب بازه زمانی"
                    className="w-full"
                    onChange={(dates) => {
                      if (dates && Array.isArray(dates) && dates.length === 2) {
                        const dateFrom = dates[0]?.format ? dates[0].format('YYYY/MM/DD') : dates[0];
                        const dateTo = dates[1]?.format ? dates[1].format('YYYY/MM/DD') : dates[1];
                        if (dateFrom && dateTo) {
                          setFilter('date_from', toGregorian(dateFrom, 'YYYY-MM-DD'));
                          setFilter('date_to', toGregorian(dateTo, 'YYYY-MM-DD'));
                        }
                      } else {
                        setFilter('date_from', '');
                        setFilter('date_to', '');
                      }
                    }}
                  />
                </div>
              </div>
            )}
            {filtersOpen && (
              <div className="flex gap-2 mt-4">
                <Button icon={<ReloadOutlined />} onClick={resetFilters} style={{ fontFamily: 'CustomFont' }}>
                  پاک کردن فیلترها
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* لیست اعلان‌ها */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          {loading && (
            <div className="p-6">
              <CardListSkeleton count={5} />
            </div>
          )}

          {!loading && isEmpty && (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="relative mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center">
                  <BellOutlined className="text-gray-400 text-2xl" />
                </div>
              </div>
              <p
                className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-1"
                style={{ fontFamily: 'CustomFont' }}
              >
                هیچ اعلانی یافت نشد
              </p>
              <p
                className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center max-w-md"
                style={{ fontFamily: 'CustomFont' }}
              >
                در حال حاضر هیچ اعلانی برای نمایش وجود ندارد
              </p>
            </div>
          )}

          {!loading && !isEmpty && (
            <div className="space-y-3 p-4">
              {notifications.map((notification, index) => {
                const isUnread = !notification.read_at;
                const notificationType = notification.notification_type || notification.type;
                const description = getNotificationDescription(notification);
                
                const menuItems = [
                  {
                    key: isUnread ? 'mark-read' : 'mark-unread',
                    label: isUnread ? 'علامت‌گذاری به عنوان خوانده شده' : 'علامت‌گذاری به عنوان خوانده نشده',
                    icon: isUnread ? <CheckCircleOutlined /> : <EyeInvisibleOutlined />,
                    onClick: () =>
                      isUnread
                        ? handleMarkAsRead(notification.id)
                        : handleMarkAsUnread(notification.id),
                  },
                  {
                    type: 'divider',
                  },
                  {
                    key: 'delete',
                    label: 'حذف',
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: () => handleDelete(notification.id),
                  },
                ];

                return (
                  <Card
                    key={notification.id}
                    className={`transition-all hover:shadow-md ${
                      isUnread
                        ? 'border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                        : 'border-l-4 border-l-transparent'
                    }`}
                    style={{
                      animationDelay: `${index * 0.05}s`,
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          {isUnread && (
                            <Badge
                              status="processing"
                              className="flex-shrink-0"
                              style={{ backgroundColor: '#3b82f6' }}
                            />
                          )}
                          <Tag
                            color={getNotificationTypeColor(notificationType)}
                            style={{ fontFamily: 'CustomFont', margin: 0 }}
                          >
                            {getNotificationTypeLabel(notificationType)}
                          </Tag>
                          <span
                            className="text-xs text-gray-500 dark:text-gray-400"
                            style={{ fontFamily: 'CustomFont' }}
                          >
                            {formatDate(notification.created_at)}
                          </span>
                        </div>

                        {/* توضیحات اعلان */}
                        {description && (
                          <p
                            className={`text-sm mb-3 ${isUnread ? 'font-medium' : ''} text-gray-800 dark:text-gray-200`}
                            style={{ fontFamily: 'CustomFont' }}
                          >
                            {description}
                          </p>
                        )}

                        {/* اطلاعات اقدام */}
                        {(notification.action_id || notification.action_number || notification.action_title) && (
                          <div className="flex items-center gap-2 mb-2">
                            <ThunderboltOutlined className="text-blue-500" />
                            <span className="text-xs text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
                              اقدام:
                            </span>
                            {notification.action_number && (
                              <Tag color="blue" style={{ fontFamily: 'CustomFont', fontSize: 11, margin: 0 }}>
                                {notification.action_number}
                              </Tag>
                            )}
                            {notification.action_title && (
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300" style={{ fontFamily: 'CustomFont' }}>
                                {notification.action_title}
                              </span>
                            )}
                          </div>
                        )}

                        {/* اطلاعات آیتم اقدام */}
                        {notification.metadata?.action_item_id && (
                          <div className="flex items-center gap-2 mb-2">
                            <FileTextOutlined className="text-emerald-500" />
                            <span className="text-xs text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
                              آیتم اقدام:
                            </span>
                            <Tag color="green" style={{ fontFamily: 'CustomFont', fontSize: 11, margin: 0 }}>
                              ID: {notification.metadata.action_item_id}
                            </Tag>
                            {notification.metadata.action_item_title && (
                              <span className="text-xs text-gray-700 dark:text-gray-300" style={{ fontFamily: 'CustomFont' }}>
                                {notification.metadata.action_item_title}
                              </span>
                            )}
                          </div>
                        )}

                        {/* اطلاعات ایجادکننده */}
                        {notification.triggered_by_name && (
                          <div className="flex items-center gap-2 mt-2">
                            <Avatar size="small" icon={<UserOutlined />}>
                              {String(notification.triggered_by_name || '').slice(0, 1) || '?'}
                            </Avatar>
                            <span className="text-xs text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
                              ایجاد شده توسط: <span className="font-medium text-gray-700 dark:text-gray-300">{notification.triggered_by_name}</span>
                            </span>
                          </div>
                        )}

                        {/* وضعیت خوانده شدن */}
                        {notification.read_at && (
                          <div className="flex items-center gap-2 mt-2">
                            <CheckCircleOutlined className="text-green-500 text-xs" />
                            <span className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
                              خوانده شده در: {formatDate(notification.read_at)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {notification.action_id && (
                          <Tooltip title="مشاهده اقدام">
                            <Button
                              type="link"
                              icon={<ThunderboltOutlined />}
                              onClick={async () => {
                                // اگر نوتیفیکیشن خوانده نشده است، آن را به عنوان خوانده شده علامت‌گذاری کن
                                if (!notification.read_at) {
                                  await handleMarkAsRead(notification.id);
                                }
                                // سپس به صفحه اقدام برو
                                navigate(`/action/${notification.action_id}`);
                              }}
                              style={{ color: '#10b981' }}
                            />
                          </Tooltip>
                        )}
                        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                          <Button type="text" icon={<MoreOutlined />} />
                        </Dropdown>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* صفحه‌بندی */}
        {!loading && !isEmpty && pagination.last_page > 1 && (
          <div className="mt-6">
            <div className="flex items-center justify-center">
              <div className="max-w-full overflow-x-auto">
                <div className="inline-flex items-center justify-center gap-2 px-1">
                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => fetchNotifications(page)}
                      className={`w-10 h-10 rounded-xl text-sm font-medium transition-all flex items-center justify-center ${
                        page === pagination.current_page
                          ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                          : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Notifications;

